import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoreUrl } from '../utils/storeUrls';
import { Check } from 'lucide-react';

const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');

  const handleDownload = () => {
    window.open(getStoreUrl(i18n.language), '_blank');
  };

  const handleContactSales = () => {
    window.location.href = '/contact';
  };

  const plans = [
    {
      name: t('pricing.plans.free.name', 'Free'),
      price: t('pricing.plans.free.price', '$0'),
      period: t('pricing.plans.free.period', 'forever'),
      features: Array.isArray(t('pricing.plans.free.features', { returnObjects: true }))
        ? (t('pricing.plans.free.features', { returnObjects: true }) as string[])
        : ['Daily limitation', 'Basic AI summaries', 'Export to text'],
      cta: t('pricing.plans.free.cta', 'Get Started'),
      highlighted: false,
      badge: null as string | null,
    },
    {
      name: 'Premium',
      price: billing === 'monthly'
        ? t('pricing.plans.standard.price', '$9')
        : t('pricing.plans.standard.priceAnnual', '$7'),
      period: billing === 'monthly'
        ? t('pricing.plans.standard.period', 'per month')
        : t('pricing.plans.standard.priceAnnualNote', 'billed annually'),
      features: Array.isArray(t('pricing.plans.standard.features', { returnObjects: true }))
        ? (t('pricing.plans.standard.features', { returnObjects: true }) as string[])
        : ['AI Whisper', 'Offline support'],
      cta: t('pricing.plans.standard.cta', 'Get Started'),
      highlighted: true,
      badge: t('pricing.plans.standard.badge', 'Most popular') as string | null,
    },
    {
      name: t('pricing.plans.enterprise.name', 'Enterprise'),
      price: t('pricing.plans.enterprise.price', 'Custom'),
      period: t('pricing.plans.enterprise.period', 'per organization'),
      features: Array.isArray(t('pricing.plans.enterprise.features', { returnObjects: true }))
        ? (t('pricing.plans.enterprise.features', { returnObjects: true }) as string[])
        : ['Custom AI models', 'API access', 'SSO integration', 'Dedicated support', 'Custom features'],
      cta: t('pricing.plans.enterprise.cta', 'Contact Sales'),
      highlighted: false,
      badge: null as string | null,
    },
  ];

  return (
    <main className="bg-navy min-h-screen px-4 py-16">
      <section className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4 text-white">{t('pricing.title')}</h1>
        <p className="text-xl text-gray-400 mb-8">
          {t('pricing.subtitle')}
        </p>

        {/* Billing toggle */}
        <div className="inline-flex bg-white/10 rounded-full p-1">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              billing === 'monthly' ? 'bg-white text-[#0F172A]' : 'text-white'
            }`}
          >
            {t('pricing.toggle.monthly', 'Monthly')}
          </button>
          <button
            onClick={() => setBilling('annual')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
              billing === 'annual' ? 'bg-white text-[#0F172A]' : 'text-white'
            }`}
          >
            {t('pricing.toggle.annual', 'Annually')}
            <span className="text-xs bg-primary/80 text-white px-2 py-0.5 rounded-full">
              {t('pricing.toggle.save', 'Save 20%')}
            </span>
          </button>
        </div>
      </section>

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-start">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative rounded-2xl p-8 ${
              plan.highlighted
                ? 'bg-primary shadow-2xl md:scale-105'
                : 'bg-white/5 border border-white/10'
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-primary text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                {plan.badge}
              </div>
            )}
            <h2 className="text-2xl font-bold mb-4 text-white">{plan.name}</h2>
            <div className="mb-6">
              <span className="text-4xl font-bold text-white">{plan.price}</span>
              <span className="text-white/60 ml-1 text-sm">/{plan.period}</span>
            </div>
            <ul className="mb-8 space-y-3">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <Check
                    className={`w-5 h-5 flex-shrink-0 ${
                      plan.highlighted ? 'text-white/80' : 'text-primary/80'
                    }`}
                  />
                  <span className="text-white/80 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            <button
              className={`w-full py-3 px-6 rounded-full font-bold transition-colors ${
                plan.highlighted
                  ? 'bg-white text-primary hover:bg-gray-50'
                  : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
              }`}
              onClick={() => {
                if (plan.cta === t('pricing.plans.enterprise.cta', 'Contact Sales')) {
                  handleContactSales();
                } else {
                  handleDownload();
                }
              }}
            >
              {plan.cta}
            </button>
          </div>
        ))}
      </div>
    </main>
  );
};

export default PricingPage;
